import { get } from 'lodash';
import { format, differenceInDays } from 'date-fns';

import getAnalyticsSectionTitle from './utils/getAnalyticsSectionTitle';
import getGTMProductListName from './utils/getGTMProductListName';
import prepareFilterValues from './utils/gtmFilterValues';
import buildProductImpressionsEvents from './utils/buildProductImpressionsEvents';
import buildPromotionClickEvent from './utils/buildPromotionClickEvent';
import {
  buildGraphicImpressionsEvents,
  buildPromotionImpressionsEvents,
} from './utils/buildPromotionImpressionsEvents';
import encodeName from './utils/encodeName';
import SectionType from 'apps/catalogue/models/SectionType';
import ProductLabel from 'apps/products/models/ProductLabel';

// gaTrackVouchers -> GOOGLE_ANALYTICS_VOUCHERS
// gaMinCheckout -> GOOGLE_ANALYTICS_MIN_CHECKOUT
// gaTrimCheckout -> GOOGLE_ANALYTICS_TRIM_CHECKOUT

export const GTM_ACTION_APPS = 'Apps';
export const GTM_ACTION_WEBSHOP = 'Webshop';
export const GTM_MAX_PRODUCTS_IN_LIST = 30;

const trimCategory = (category, level) => {
  if (typeof category === 'string') {
    return category.split('/').slice(0, level).join('/');
  }
  return category;
};
const getEventValueForPurchasedEvent = (eventAction) => {
  switch (eventAction) {
    case 'orderPurchased':
      return 1;
    case 'orderCancelled':
      return -1;
    default:
      return 0;
  }
};

const gtmEventHandlers = {
  acceptedEvents: {
    'gtm:addToTrolley': 'sendAddToTrolleyEvent',
    'gtm:basketChangedEvent': 'sendBasketChangedEvent',
    'gtm:basketAddEvent': 'sendAddToBasketEvent',
    'gtm:checkout': 'sendCheckoutEvent',
    // 'gtm:checkoutWalk': 'sendCheckoutWalkEvent', // TODO
    'gtm:confirmationAdClick': 'sendConfirmationAdClick',
    'gtm:consentsChangedEvent': 'sendMarketingConsentsChangeEvent',
    'gtm:firstCheckout': 'sendFirstCheckoutEvent',
    // 'gtm:FacebookLiked': 'sendFacebookLikeEvent', // TODO
    // 'gtm:FacebookUnliked': 'sendFacebookUnlikeEvent', // TODO
    'gtm:graphicImpressions': 'sendGraphicImpressions',
    'gtm:login': 'sendLoginEvent',
    'gtm:loginType': 'sendLoginTypeEvent',
    'gtm:oosRatio': 'sendOosRatioEvent',
    // 'gtm:orderStatus': 'sendOrderStatus', // TODO
    'gtm:orderPurchased': 'sendOrderPurchasedEvents',
    'gtm:orderCancelled': 'sendOrderCancelledEvents',
    'gtm:orderConfirmationRule': 'sendOrderConfirmationRuleEvent',
    // 'gtm:productDetails': 'sendProductDetailsEvent', // TODO
    // 'gtm:PinnedIt': 'sendPinnedItEvent', // TODO
    'gtm:productImpressions': 'sendProductImpressions',
    'gtm:productClick': 'sendProductClickEvent',
    'gtm:promotionImpressions': 'sendPromotionImpressions',
    'gtm:promotionClick': 'sendPromotionClickEvent',
    'gtm:registration': 'sendRegistrationEvent',
    'gtm:registrationType': 'sendRegistrationTypeEvent',
    'gtm:removeFromTrolley': 'sendRemoveFromTrolleyEvent',
    'gtm:secondCheckoutWithinFourWeeksEvent': 'sendSecondCheckoutWithinFourWeeksEvent',
    'gtm:singleProductImpression': 'sendSingleProductImpression',
    'gtm:uaEvent': 'sendUniversalAnalyticsEvent',
    'gtm:uaCheckout': 'sendUACheckoutEvent',
    'gtm:uaFirstCheckout': 'sendUAFirstCheckoutEvent',
    'gtm:virtualPageView': 'sendVirtualPageViewEvent',
    'gtm:pageView': 'sendPageViewEvent',
    'gtm:ordersReserved': 'sendOrdersReservedEvent',
    'gtm:navigationEvent': 'sendNavigationInteractionEvent',
    'gtm:editOrder': 'sendEditOrderEvent',
  },

  sendGTMEvent(gtmEvent) {
    if (typeof window.dataLayer !== 'undefined') {
      const state = (this && this.getState) ? this.getState() : {};
      const placedAtLeastOneMobileOrder = get(state, 'user.shoppingDetails.placedAtLeastOneMobileOrder');
      window.dataLayer.push({
        isApp: placedAtLeastOneMobileOrder,
        ...gtmEvent,
      });
    }
  },

  hasBetaFeature(betaFeatureName) {
    return this.getState().user.betaFeatures.includes(betaFeatureName);
  },

  sendUniversalAnalyticsEvent(eventData) {
    const uaEvent = {
      event: 'UAEvent',
      uaEventCategory: eventData.category,
      uaEventAction: eventData.action,
      uaEventLabel: eventData.label,
      uaEventValue: eventData.value,
    };
    this.sendGTMEvent(uaEvent);
  },

  sendCheckoutEvent(eventData) {
    const event = {
      event: 'Checkout',
      uaEventCategory: 'Checkout',
      uaEventAction: eventData.action,
      uaEventLabel: eventData.label,
    };
    this.sendGTMEvent(event);
  },

  sendFirstCheckoutEvent(eventData) {
    const event = {
      event: 'FirstCheckout',
      uaEventCategory: 'FirstCheckout',
      uaEventAction: eventData.action,
      uaEventLabel: eventData.label,
    };
    this.sendGTMEvent(event);
  },

  sendSecondCheckoutWithinFourWeeksEvent(eventData) {
    const event = {
      event: 'SecondCheckoutWithinFourWeeks',
      uaEventCategory: 'SecondCheckoutWithinFourWeeks',
      uaEventAction: eventData.action,
      uaEventLabel: eventData.label,
    };
    this.sendGTMEvent(event);
  },

  sendUACheckoutEvent(eventData) {
    const uaEvent = {
      event: 'UAEvent',
      uaEventCategory: 'Checkout',
      uaEventAction: 'order-placed',
      uaEventLabel: eventData.label,
      uaEventValue: eventData.value,
    };
    this.sendGTMEvent(uaEvent);
  },

  sendUAFirstCheckoutEvent() {
    const uaEvent = {
      event: 'UAEvent',
      uaEventCategory: 'Checkout',
      uaEventAction: 'first-order-placed',
    };
    this.sendGTMEvent(uaEvent);
  },

  sendOrderConfirmationRuleEvent() {
    const event = {
      event: 'OrderConfirmationRule',
    };
    this.sendGTMEvent(event);
  },

  sendConfirmationAdClick(eventData) {
    const event = {
      event: 'UAEvent',
      uaEventCategory: 'Checkout',
      uaEventAction: 'confirmation-ad-clicked',
      uaEventLabel: eventData.label,
    };
    this.sendGTMEvent(event);
  },

  sendVirtualPageViewEvent(virtualPageURL) {
    const event = {
      event: 'virtualPageView',
      virtualPageURL,
    };

    this.sendGTMEvent(event);
  },

  sendRegistrationEvent(source, client) {
    const event = {
      event: 'Registration',
      uaEventCategory: 'Registration',
      uaEventAction: source,
      uaEventLabel: client,
    };
    this.sendGTMEvent(event);
  },

  sendRegistrationTypeEvent(source, type) {
    const event = {
      event: 'RegistrationType',
      uaEventCategory: 'RegistrationType',
      uaEventAction: source,
      uaEventLabel: type,
    };
    this.sendGTMEvent(event);
  },

  sendLoginEvent({ source, client, ...eventData }) {
    const event = {
      event: 'Login',
      uaEventCategory: 'Login',
      uaEventAction: source,
      uaEventLabel: client,
      ...eventData,
    };

    this.sendGTMEvent(event);
  },

  sendLoginTypeEvent(source, type) {
    const event = {
      event: 'LoginType',
      uaEventCategory: 'LoginType',
      uaEventAction: source,
      uaEventLabel: type,
    };
    this.sendGTMEvent(event);
  },

  sendBasketChangedEvent({ basketValue, emptyBasket }) {
    const event = {
      event: 'BasketChangedRule',
      BV: basketValue,
      EB: emptyBasket,
    };
    this.sendGTMEvent(event);
  },

  sendAddToBasketEvent(sku) {
    const event = {
      event: 'ProductAddToBasketRule',
      PID: sku,
    };
    this.sendGTMEvent(event);
  },

  sendProductClickEvent(productData, eventCallback) {
    const {
      section,
      product,
      gtmPageType,
      collectionFopIdx,
      filters = [],
    } = productData;
    const sectionTitle = getAnalyticsSectionTitle(section, gtmPageType);
    const list = getGTMProductListName(gtmPageType, section);
    const isNumberOfResultsIncluded = ['Offers', 'Catalogue', 'Search results'].includes(gtmPageType);
    const numberOfResults = get(productData, 'section.fops.length');
    const filterValues = prepareFilterValues(filters);
    const productLabels = get(product, 'labels', []);
    const productRating = get(product, 'reviewStats.averageRate', 0);
    const productOffer = get(product, 'offer', {});
    const sectionType = get(section, 'attributes.type');

    const event = {
      event: 'productClick',
      ...(filterValues && { filterValues }),
      ...(isNumberOfResultsIncluded && { numberOfResults }),
      pageTypeDimension: gtmPageType,
      productSectionDimension: sectionTitle,
      ecommerce: {
        currencyCode: 'GBP',
        click: {
          actionField: {
            list,
          },
          products: [{
            id: product.sku,
            name: encodeName(product.name),
            brand: product.brand.name,
            category: product.mainCategory,
            price: product.price.current,
            position: collectionFopIdx,
            list,
            dimension60: productRating > 0 ? productRating.toFixed(1) : undefined, // product rating
            promotionNames: get(productOffer, 'text'),
            dimension70: get(product, 'coupon.id'), // product coupon
            canAddToBasket: !productLabels.includes(ProductLabel.OOS),
            isFavorite: productLabels.includes(ProductLabel.FAVOURITE),
            isFeatured: sectionType === SectionType.FEATURED,
            isOnPromotion: productLabels.includes(ProductLabel.ON_OFFER),
            promotionIds: get(productOffer, 'id'),
          }],
        },
      },
      uaEventCategory: 'E-Commerce',
      uaEventAction: 'productClick',
      uaEventLabel: `${gtmPageType}|${sectionTitle}`,
      uaEventValue: 1,
    };

    if (eventCallback) {
      event.eventCallback = eventCallback;
      event.eventTimeout = 2000;
    }

    this.sendGTMEvent(event);
  },

  sendProductImpressions(productImpressionsData) {
    const gaMinProductImpressions = this.shouldUseMinProductImpressions();
    const impressionsEvents = buildProductImpressionsEvents(productImpressionsData, gaMinProductImpressions);

    impressionsEvents.forEach(this.sendGTMEvent, this);
  },

  shouldUseMinProductImpressions() {
    return this.hasBetaFeature('GOOGLE_ANALYTICS_MIN_PROD_IMPRES');
  },

  shouldTrackPromoImpressions() {
    return this.hasBetaFeature('GOOGLE_ANALYTICS_PROMO_TRACKING');
  },

  shouldTrimGACheckoutInfo() {
    return this.hasBetaFeature('GOOGLE_ANALYTICS_TRIM_CHECKOUT');
  },

  isGAMinCheckoutEnabled() {
    return this.hasBetaFeature('GOOGLE_ANALYTICS_MIN_CHECKOUT');
  },

  sendPromotionImpressions(promotionImpressionsData) {
    if (this.shouldTrackPromoImpressions()) {
      const impressionsEvents = buildPromotionImpressionsEvents(promotionImpressionsData);
      impressionsEvents.forEach(this.sendGTMEvent, this);
    }
  },

  sendGraphicImpressions(graphicImpressionsData) {
    const impressionsEvents = buildGraphicImpressionsEvents(graphicImpressionsData);
    impressionsEvents.forEach(this.sendGTMEvent);
  },

  sendPromotionClickEvent(promotionData, eventCallback) {
    if (this.shouldTrackPromoImpressions()) {
      const event = buildPromotionClickEvent(promotionData, eventCallback);

      this.sendGTMEvent(event);
    }
  },

  sendOosRatioEvent(data) {
    const { outOfStockStatistics = [], gtmPageType } = data;

    outOfStockStatistics.forEach(oos => {
      const event = {
        event: 'oosProductsEvent',
        uaEventCategory: 'Out of stock',
        uaEventAction: gtmPageType,
        uaEventLabel: `${oos.oosRatio}%`,
        productCategoryDimension: oos.category,
      };
      this.sendGTMEvent(event);
    });
  },

  mapProductsDTO(products, transformQuantityFunction = x => x) {
    if (this.isGAMinCheckoutEnabled()) {
      return products.map(product => ({
        id: product.sku,
        price: product.price,
        quantity: transformQuantityFunction(product.quantity),
      }));
    } else if (this.shouldTrimGACheckoutInfo()) {
      return products.map(product => ({
        id: product.sku,
        name: product.name,
        price: product.price,
        category: trimCategory(product.category, 3),
        quantity: transformQuantityFunction(product.quantity),
      }));
    } else {
      return products.map((product) => {
        const productOffer = get(product, 'offer', {});
        const productLabels = get(product, 'labels', []);
        const productRating = get(product, 'reviewStats.averageRate', 0);
        const packInfo = get(product, 'packInfo', {});
        const dietaryFlags = [
          ...(packInfo.vegetarian ? ['Vegetarian'] : []),
          ...(packInfo.organic ? ['Organic'] : []),
        ].join(', ');

        return {
          id: product.sku,
          name: product.name,
          brand: product.brand,
          price: product.price,
          category: product.category,
          quantity: transformQuantityFunction(product.quantity),
          dimension72: undefined, // available on BOP only
          list: 'Purchased order',
          coupon: get(product, 'coupon.id'),
          dimension60: productRating > 0 ? productRating.toFixed(1) : undefined, // product rating
          promotionNames: get(productOffer, 'text'),
          dimension62: 1, // image count
          dimension63: undefined, // video count
          dimension64: undefined, // product description
          dimension65: undefined, // nutritional information
          dimension66: dietaryFlags, // dietary flags
          dimension67: undefined, // ingredients
          dimension68: undefined, // usage
          dimension69: get(product, 'fullPrice'), // full price
          canAddToBasket: !productLabels.includes(ProductLabel.OOS),
          isFavorite: productLabels.includes(ProductLabel.FAVOURITE),
          isOnPromotion: productLabels.includes(ProductLabel.ON_OFFER),
          promotionIds: get(productOffer, 'id'),
        };
      });
    }
  },

  calculateProductsPrice(products = []) {
    return products.reduce(
      // eslint-disable-next-line no-return-assign, no-param-reassign
      (accu, item) => (accu += item.price * item.quantity),
      0
    );
  },

  createPurchasedOrderEvents(purchasedOrderDTO, transformMoneyFunction, eventAction) {
    const productsDTO = this.mapProductsDTO(purchasedOrderDTO.products, transformMoneyFunction);
    const timestamp = String(Date.now());
    const productsPrice = this.calculateProductsPrice(purchasedOrderDTO.products);
    const totalCostWOShippeng = purchasedOrderDTO.totalCost - purchasedOrderDTO.shipping;
    const voucherDiscount = Math.round((productsPrice - totalCostWOShippeng) * 100) / 100;

    let getAffiliationDescription = () => timestamp;
    if (GTM_MAX_PRODUCTS_IN_LIST < productsDTO.length) {
      getAffiliationDescription = (idx) => `${timestamp}_${idx}`;
    }

    const chunkedEvents = [];
    const orderTypeBaseName = purchasedOrderDTO.editing ? 'edited-order' : 'new-order';

    for (let beginIdx = 0; beginIdx < productsDTO.length; beginIdx += GTM_MAX_PRODUCTS_IN_LIST) {
      const chunkedProductsDTO = productsDTO.slice(beginIdx, beginIdx + GTM_MAX_PRODUCTS_IN_LIST);
      const orderType = `${orderTypeBaseName}${beginIdx > 0 ? '-continued' : ''}`;

      const eventData = {
        uaEventCategory: 'E-Commerce',
        uaEventAction: eventAction,
        uaEventLabel: orderType,
        uaEventValue: beginIdx > 0 ? 0 : getEventValueForPurchasedEvent(eventAction),
        event: 'eecPlaceOrder',
        eecOrderType: orderType,
        street: get(purchasedOrderDTO, 'address.street') || get(purchasedOrderDTO, 'address.street2'),
        city: get(purchasedOrderDTO, 'address.city'),
        area: get(purchasedOrderDTO, 'address.area'),
        country: get(purchasedOrderDTO, 'address.country'),
        postcode: get(purchasedOrderDTO, 'address.postcode'),
        firstOrder: purchasedOrderDTO.firstShop,
        slotBookingTime: format(get(purchasedOrderDTO, 'deliverySlot.startTime'), 'HH:mm'),
        slotBookingDate: format(get(purchasedOrderDTO, 'deliverySlot.startTime'), 'YYYY-MM-DD'),
        slotBookingDay: format(get(purchasedOrderDTO, 'deliverySlot.startTime'), 'dddd'),
        vanType: get(purchasedOrderDTO, 'deliverySlot.vanType'),
        numberOfDaysUntilDelivery: differenceInDays(get(purchasedOrderDTO, 'deliverySlot.endTime'), new Date()),
        voucherDiscount: beginIdx > 0 ? 0 : voucherDiscount,
        couponDiscountAmount: purchasedOrderDTO.couponDiscountAmount,
        basketId: purchasedOrderDTO.basketId,
        editOrderDeadline: format(get(purchasedOrderDTO, 'deliverySlot.cutoffTime'), 'YYYY-MM-DD'),
        ecommerce: {
          currencyCode: 'GBP',
          purchase: {
            actionField: {
              id: purchasedOrderDTO.orderId,
              affiliation: getAffiliationDescription(beginIdx / GTM_MAX_PRODUCTS_IN_LIST),
              revenue: beginIdx > 0 ? 0 : transformMoneyFunction(purchasedOrderDTO.totalCost),
              shipping: beginIdx > 0 ? 0 : transformMoneyFunction(purchasedOrderDTO.shipping),
              tax: purchasedOrderDTO.tax,
            },
            products: chunkedProductsDTO,
          },
        },
      };

      if (purchasedOrderDTO.voucherIds) {
        eventData.ecommerce.purchase.actionField.coupon = beginIdx > 0 ? null : purchasedOrderDTO.voucherIds.join(',');
      }

      chunkedEvents.push(eventData);
    }

    return chunkedEvents;
  },

  sendOrderPurchasedEvents(purchasedOrderDTO) {
    const purchasedEvents = this.createPurchasedOrderEvents(purchasedOrderDTO, x => x, 'orderPurchased');
    purchasedEvents.map(this.sendGTMEvent);
  },

  sendOrderCancelledEvents(cancelledOrderDTO) {
    const revert = x => -x;
    const purchasedCancelledEvents = this.createPurchasedOrderEvents(cancelledOrderDTO, revert, 'orderCancelled');
    purchasedCancelledEvents.map(this.sendGTMEvent);
  },

  sendEditOrderEvent(eventData) {
    const event = {
      event: 'order_update',
      updateType: 'Edit order clicked',
      ...eventData,
    };

    this.sendGTMEvent(event);
  },

  sendAddToTrolleyEvent(productData) {
    this.sendTrolleyEvent('addToTrolley', productData);
  },

  sendRemoveFromTrolleyEvent(productData) {
    this.sendTrolleyEvent('removeFromTrolley', productData);
  },

  sendTrolleyEvent(type, productData) {
    const {
      gtmPageType,
      section,
      product,
      productDetails, // Optional, available in events sent from BOP
      quantity,
      containerType,
      cartActionType,
      basket,
      collectionFopIdx,
    } = productData;
    const sectionTitle = getAnalyticsSectionTitle(section, gtmPageType, containerType);
    const list = getGTMProductListName(gtmPageType, section, containerType);
    const itemListIndex = typeof collectionFopIdx === 'number' && !Number.isNaN(collectionFopIdx)
      ?
      collectionFopIdx + 1
      :
      undefined;

    const actionFieldObject = type === 'addToTrolley' ? 'add' : 'remove';
    const cartActionTypeName = type === 'addToTrolley' ? 'addToCartType' : 'removeFromCartType';
    const productLabels = get(product, 'labels', []);
    const productRating = get(product, 'reviewStats.averageRate', 0);
    const productOffer = get(product, 'offer', {});
    const sectionType = get(section, 'attributes.type');
    const packInfo = get(product, 'packInfo', {});
    const productDescription = get(productDetails, 'backOfPack.bopFields.description', [])
      .map(item => item.content)
      .join(' ');
    const productNutrition = get(productDetails, 'backOfPack.bopFields.nutrition', [])
      .map(item => item.content)
      .join(' ');
    const productIngredients = get(productDetails, 'backOfPack.bopFields.ingredients', [])
      .map(item => item.content)
      .join(' ');
    const productUsage = get(productDetails, 'backOfPack.bopFields.storageAndUsage', [])
      .map(item => item.content)
      .join(' ');

    const dietaryFlags = [
      ...(packInfo.vegetarian ? ['Vegetarian'] : []),
      ...(packInfo.organic ? ['Organic'] : []),
    ].join(', ');
    const event = {
      event: type,
      [cartActionTypeName]: cartActionType,
      basketId: get(basket, 'basketId'),
      basketValue: get(basket, 'value'),
      pageTypeDimension: gtmPageType,
      productSectionDimension: sectionTitle,
      ecommerce: {
        currencyCode: 'GBP',
        [actionFieldObject]: {
          actionField: {
            list,
            action: actionFieldObject,
          },
          products: [{
            id: product.sku,
            name: encodeName(product.name),
            brand: get(product, 'brand.name'),
            category: product.mainCategory,
            price: get(product, 'price.current'),
            quantity,
            list,
            dimension60: productRating > 0 ? productRating.toFixed(1) : undefined, // product rating
            promotionNames: get(productOffer, 'text'),
            dimension62: get(productDetails, 'backOfPack.images.length'), // product image count
            dimension63: get(productDetails, 'backOfPack.videos.length'), // product video count
            dimension64: productDescription.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product description
            dimension65: productNutrition.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product nutritional information
            dimension66: dietaryFlags, // product dietary flags
            dimension67: productIngredients.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product ingredients
            dimension68: productUsage.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product usage
            dimension69: get(product, 'price.previous'), // product full price
            dimension71: cartActionType, // add to cart type
            dimension70: get(product, 'coupon.id'), // product coupon
            dimension72: product.derivativeSku, // child product SKU (on BOP only)
            variant: product.optionName, // child product name (on BOP only)
            canAddToBasket: !productLabels.includes(ProductLabel.OOS),
            isFavorite: productLabels.includes(ProductLabel.FAVOURITE),
            isFeatured: sectionType === SectionType.FEATURED,
            isOnPromotion: productLabels.includes(ProductLabel.ON_OFFER),
            promotionIds: get(productOffer, 'id'),
            itemListIndex,
          }],
        },
      },
      uaEventCategory: 'E-Commerce',
      uaEventAction: type,
      uaEventLabel: `${gtmPageType}|${sectionTitle}`,
      uaEventValue: 1,
    };

    this.sendGTMEvent(event);
  },

  sendMarketingConsentsChangeEvent(consentsData) {
    const event = {
      event: 'consentsChangedEvent',
      MCC: consentsData,
    };

    this.sendGTMEvent(event);
  },

  sendSingleProductImpression(product) {
    const event = {
      event: 'eecGeneric',
      pageTypeDimension: 'Back of pack',
      ecommerce: {
        currencyCode: 'GBP',
        detail: {
          actionField: {},
          products: [{
            ...product,
            list: 'Back of pack',
          }],
        },
      },
      uaEventCategory: 'E-Commerce',
      uaEventAction: 'productDetails',
      uaEventLabel: 'Back of pack|Back of pack',
      uaEventValue: 1,
    };

    this.sendGTMEvent(event);
  },

  sendPageViewEvent(data) {
    const eventData = {
      ...data,
      event: 'pageview',
      isOSP: false,
    };

    this.sendGTMEvent(eventData);
  },

  sendOrdersReservedEvent(data) {
    const eventData = {
      event: 'orders_reserved',
      ...data,
    };

    this.sendGTMEvent(eventData);
  },

  sendNavigationInteractionEvent(data) {
    const eventData = {
      event: 'navigation_interaction',
      ...data,
    };

    this.sendGTMEvent(eventData);
  },
};

export default gtmEventHandlers;
